import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Plus, Trash2, Download, Mail, Loader2, Copy, Check, Eye, BookmarkPlus, X, ImagePlus, ZoomIn, ZoomOut, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-hairline rounded-lg bg-page px-3.5 py-2.5 font-mono text-xs text-ink placeholder:text-mist focus:outline-none focus:border-azure transition-colors duration-300";

const CALENDAR_CLASSNAMES = {
  caption_label: "font-medium text-sm text-ink",
  nav_button: "rounded-full border border-hairline h-7 w-7 bg-white hover:border-ink transition-colors duration-200",
  head_cell: "font-mono text-[9px] uppercase tracking-[0.15em] text-mist rounded-md w-8 font-normal",
  day: "h-8 w-8 p-0 font-mono text-xs text-ink rounded-full hover:bg-page transition-colors duration-150",
  day_selected: "bg-[#002B5B] text-white hover:bg-[#002B5B] focus:bg-[#002B5B] rounded-full",
  day_today: "text-[#8A6335] font-bold",
  day_outside: "text-mist/50",
};

const fmtDueDate = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-US", { month: "long", day: "2-digit", year: "numeric" });
};

const labelCls = "block font-mono text-[9px] uppercase tracking-[0.18em] text-mist mb-1.5";

const newItem = () => ({ description: "", quantity: 1, unit_price: "" });

const money = (n) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const InvoiceForm = ({ token, onComplete, guest = false, guestToken = "", guestDone = false, onGuestDone = null, onGuestLocked = null }) => {
  const [fromName, setFromName] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [taxRate, setTaxRate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [dueOpen, setDueOpen] = useState(false);
  const [paymentTerms, setPaymentTerms] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([newItem()]);
  const [templates, setTemplates] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateBusy, setTemplateBusy] = useState(false);
  const [logoData, setLogoData] = useState("");
  const [zoom, setZoom] = useState(1);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [sentLink, setSentLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewTimer = useRef(null);
  const previewSeq = useRef(0);

  const auth = token ? { headers: { Authorization: `Bearer ${token}` }, withCredentials: true } : { withCredentials: true };

  const payload = () => ({
    from_name: fromName,
    from_email: fromEmail,
    client_name: clientName,
    client_email: clientEmail || null,
    invoice_number: invoiceNumber,
    tax_rate: parseFloat(taxRate) || 0,
    due_date: dueDate,
    payment_terms: paymentTerms,
    logo_b64: logoData || "",
    notes,
    origin_url: window.location.origin,
    ...(guest && !token ? { guest_id: guestToken } : {}),
    items: items.map((i) => ({
      description: i.description,
      quantity: parseFloat(i.quantity) || 0,
      unit_price: parseFloat(i.unit_price) || 0,
    })),
  });

  const valid =
    fromName.trim() &&
    clientName.trim() &&
    items.length > 0 &&
    items.every((i) => i.description.trim() && parseFloat(i.quantity) > 0 && parseFloat(i.unit_price) >= 0 && i.unit_price !== "");

  const missing = [
    !fromName.trim() && "your name",
    !clientName.trim() && "client name",
    (!items.length || !items.every((i) => i.description.trim() && i.unit_price !== "" && parseFloat(i.quantity) > 0)) && "one complete line item",
  ].filter(Boolean);

  const subtotal = items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0);
  const tax = subtotal * ((parseFloat(taxRate) || 0) / 100);

  // Debounced live preview — re-renders page 1 as the form changes.
  useEffect(() => {
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      const seq = ++previewSeq.current;
      setPreviewLoading(true);
      try {
        const res = await axios.post(`${API}/pdf/invoice/preview`, payload(), { ...auth, responseType: "blob" });
        if (seq !== previewSeq.current) return;
        const url = URL.createObjectURL(res.data);
        setPreviewUrl((old) => {
          if (old) URL.revokeObjectURL(old);
          return url;
        });
      } catch {
        // keep the last good preview on rate limits / network blips
      } finally {
        if (seq === previewSeq.current) setPreviewLoading(false);
      }
    }, 700);
    return () => clearTimeout(previewTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromName, fromEmail, clientName, clientEmail, invoiceNumber, taxRate, dueDate, paymentTerms, notes, items, logoData, token, guestToken]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  // Saved templates (workspace members only)
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/pdf/invoice/templates`, auth)
      .then((res) => setTemplates(res.data.templates))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const applyTemplate = (t) => {
    setFromName(t.from_name || "");
    setFromEmail(t.from_email || "");
    setTaxRate(t.tax_rate ? String(t.tax_rate) : "");
    setNotes(t.notes || "");
    setPaymentTerms(t.payment_terms || "");
    setLogoData(t.logo_b64 ? `data:image/png;base64,${t.logo_b64}` : "");
    if (t.items?.length) {
      setItems(t.items.map((i) => ({ description: i.description, quantity: i.quantity, unit_price: String(i.unit_price) })));
    }
    toast.success(`Template “${t.name}” applied`);
  };

  const saveTemplate = async () => {
    const name = templateName.trim();
    if (!name || templateBusy) return;
    setTemplateBusy(true);
    try {
      const res = await axios.post(`${API}/pdf/invoice/templates`, {
        name,
        from_name: fromName,
        from_email: fromEmail,
        tax_rate: parseFloat(taxRate) || 0,
        notes,
        payment_terms: paymentTerms,
        logo_b64: logoData || "",
        items: items.map((i) => ({ description: i.description, quantity: parseFloat(i.quantity) || 0, unit_price: parseFloat(i.unit_price) || 0 })),
      }, auth);
      setTemplates([res.data, ...templates]);
      setSaveOpen(false);
      setTemplateName("");
      toast.success(`Template “${name}” saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save template");
    } finally {
      setTemplateBusy(false);
    }
  };

  const deleteTemplate = async (t) => {
    try {
      await axios.delete(`${API}/pdf/invoice/templates/${t.id}`, auth);
      setTemplates(templates.filter((x) => x.id !== t.id));
      toast.success(`Template “${t.name}” deleted`);
    } catch {
      toast.error("Could not delete template");
    }
  };

  const fail = (msg) => {
    setError(msg);
    setBusy(null);
  };

  const handleLocked = (status) => {
    if (guest && status === 402 && onGuestLocked) {
      onGuestLocked();
      return true;
    }
    return false;
  };

  const download = async () => {
    if (!valid || busy) return;
    setBusy("download");
    setError(null);
    try {
      const res = await axios.post(`${API}/pdf/invoice`, payload(), { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoiceNumber.trim() || "invoice"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      if (guest && onGuestDone) onGuestDone();
      if (onComplete) onComplete();
    } catch (e) {
      let msg = "Could not build the invoice.";
      let status = e.response?.status;
      if (e.response?.data instanceof Blob) {
        try {
          msg = JSON.parse(await e.response.data.text()).detail || msg;
        } catch {}
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      if (handleLocked(status)) { setBusy(null); return; }
      fail(msg);
      return;
    }
    setBusy(null);
  };

  const emailInvoice = async () => {
    if (!valid || busy) return;
    if (!clientEmail.trim()) {
      setError("Add the client email to send the invoice.");
      return;
    }
    setBusy("email");
    setError(null);
    setSentLink(null);
    try {
      const res = await axios.post(`${API}/pdf/invoice/email`, payload(), auth);
      setSentLink(res.data.link);
      toast.success(`Invoice emailed to ${clientEmail.trim()}`);
      if (onComplete) onComplete();
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === "string" ? d : "Invoice email could not be sent — download the PDF instead.");
    }
    setBusy(null);
  };

  const copyLink = () => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(sentLink)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        })
        .catch(() => window.prompt("Copy invoice link", sentLink));
    } else {
      window.prompt("Copy invoice link", sentLink);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="px-5 pt-4 overflow-hidden" data-testid="invoice-engine">
      <div className="border border-hairline rounded-lg bg-page p-4 space-y-4">

        <div data-testid="invoice-preview-wrap">
          <div className="flex items-center justify-between mb-1.5">
            <span className={`${labelCls} mb-0`}>Live preview</span>
            <div className="flex items-center gap-1" data-testid="invoice-zoom-controls">
              <button type="button" data-testid="preview-zoom-out" aria-label="Zoom out"
                onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}
                disabled={zoom <= 0.5}
                className="w-7 h-7 flex items-center justify-center rounded-full border border-hairline text-fog hover:text-ink hover:border-ink disabled:opacity-30 transition-colors duration-200">
                <ZoomOut className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
              <span className="font-mono text-[10px] text-mist w-10 text-center tabular-nums" data-testid="preview-zoom-level">{Math.round(zoom * 100)}%</span>
              <button type="button" data-testid="preview-zoom-in" aria-label="Zoom in"
                onClick={() => setZoom((z) => Math.min(2, +(z + 0.25).toFixed(2)))}
                disabled={zoom >= 2}
                className="w-7 h-7 flex items-center justify-center rounded-full border border-hairline text-fog hover:text-ink hover:border-ink disabled:opacity-30 transition-colors duration-200">
                <ZoomIn className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>
          </div>
          <div className="relative border border-hairline rounded-lg bg-white overflow-auto h-[340px]" data-testid="invoice-preview">
            {previewUrl ? (
              <div className="min-h-full flex items-start justify-center p-2">
                <img src={previewUrl} alt="Invoice preview" style={{ height: 324 * zoom }} className="block w-auto max-w-none" data-testid="invoice-preview-img" />
              </div>
            ) : (
              <div className="py-14 text-center" data-testid="invoice-preview-empty">
                <Eye className="w-5 h-5 text-mist mx-auto mb-2" strokeWidth={1.5} />
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-mist">Your invoice renders here as you type</p>
              </div>
            )}
            {previewLoading && previewUrl && (
              <span className="absolute top-2 right-2 flex items-center gap-1 bg-ink/80 text-white rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em]" data-testid="invoice-preview-updating">
                <Loader2 className="w-2.5 h-2.5 animate-spin" strokeWidth={2} /> Updating
              </span>
            )}
          </div>
        </div>

        {token && (
          <div data-testid="invoice-templates">
            <span className={labelCls}>Saved templates</span>
            <div className="flex items-center gap-2 flex-wrap">
              {templates.map((t, i) => (
                <span key={t.id} className="group inline-flex items-center gap-1 border border-hairline rounded-full bg-white pl-3 pr-1.5 py-1.5" data-testid={`invoice-template-${i}`}>
                  <button type="button" onClick={() => applyTemplate(t)} data-testid={`invoice-template-apply-${i}`}
                    className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink hover:text-azure transition-colors duration-200">
                    {t.name}
                  </button>
                  <button type="button" onClick={() => deleteTemplate(t)} data-testid={`invoice-template-delete-${i}`} aria-label={`Delete template ${t.name}`}
                    className="w-4 h-4 flex items-center justify-center rounded-full text-mist hover:text-[#FF3333] opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <X className="w-3 h-3" strokeWidth={2} />
                  </button>
                </span>
              ))}
              {templates.length === 0 && !saveOpen && (
                <span className="font-mono text-[10px] text-mist" data-testid="invoice-templates-empty">No templates yet — save your sender details and line items for one-tap reuse.</span>
              )}
              {saveOpen ? (
                <span className="inline-flex items-center gap-1.5">
                  <input
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveTemplate()}
                    placeholder="Template name"
                    maxLength={60}
                    autoFocus
                    data-testid="invoice-template-name"
                    className="border border-hairline rounded-full bg-white px-3 py-1.5 font-mono text-[10px] text-ink placeholder:text-mist focus:outline-none focus:border-azure w-36 transition-colors duration-300"
                  />
                  <button type="button" onClick={saveTemplate} disabled={!templateName.trim() || templateBusy} data-testid="invoice-template-save"
                    className="border border-hairline rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-azure hover:text-ink hover:border-ink disabled:opacity-40 transition-colors duration-200">
                    {templateBusy ? "Saving…" : "Save"}
                  </button>
                  <button type="button" onClick={() => setSaveOpen(false)} aria-label="Cancel" className="w-6 h-6 flex items-center justify-center rounded-full text-mist hover:text-ink hover:bg-black/5 transition-colors duration-200">
                    <X className="w-3.5 h-3.5" strokeWidth={2} />
                  </button>
                </span>
              ) : (
                <button type="button" onClick={() => setSaveOpen(true)} data-testid="invoice-template-open-save"
                  className="inline-flex items-center gap-1.5 border border-hairline rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-azure hover:text-ink hover:border-ink transition-colors duration-200">
                  <BookmarkPlus className="w-3.5 h-3.5" strokeWidth={1.5} /> Save as template
                </button>
              )}
            </div>
          </div>
        )}

        <div data-testid="invoice-logo-row">
          <span className={labelCls}>Company logo (optional — PNG or JPG, max 700KB)</span>
          {logoData ? (
            <div className="inline-flex items-center gap-3 border border-hairline rounded-lg bg-white px-3 py-2" data-testid="invoice-logo-preview">
              <img src={logoData} alt="Company logo" className="h-9 w-auto max-w-[140px] object-contain" />
              <button type="button" onClick={() => setLogoData("")} data-testid="invoice-logo-remove" aria-label="Remove logo"
                className="w-6 h-6 flex items-center justify-center rounded-full text-mist hover:text-[#FF3333] hover:bg-black/5 transition-colors duration-200">
                <X className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            </div>
          ) : (
            <label data-testid="invoice-logo-upload"
              className="inline-flex items-center gap-2 border border-dashed border-hairline rounded-lg bg-page px-3.5 py-2.5 cursor-pointer hover:border-azure transition-colors duration-300">
              <ImagePlus className="w-4 h-4 text-mist" strokeWidth={1.5} />
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fog">Upload logo</span>
              <input
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                data-testid="invoice-logo-input"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (!f) return;
                  if (!["image/png", "image/jpeg"].includes(f.type)) {
                    toast.error("Logo must be a PNG or JPG image");
                    return;
                  }
                  if (f.size > 700 * 1024) {
                    toast.error("Logo must be under 700KB");
                    return;
                  }
                  const reader = new FileReader();
                  reader.onload = () => setLogoData(String(reader.result));
                  reader.readAsDataURL(f);
                }}
              />
            </label>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="invoice-from-name" className={labelCls}>From — your name or company *</label>
            <input id="invoice-from-name" value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Acme Advisory LLC" maxLength={120} data-testid="invoice-from-name" className={inputCls} />
          </div>
          <div>
            <label htmlFor="invoice-from-email" className={labelCls}>Your email (optional)</label>
            <input id="invoice-from-email" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder="you@company.com" maxLength={120} data-testid="invoice-from-email" className={inputCls} />
          </div>
          <div>
            <label htmlFor="invoice-client-name" className={labelCls}>Bill to — client name *</label>
            <input id="invoice-client-name" value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Client Co" maxLength={120} data-testid="invoice-client-name" className={inputCls} />
          </div>
          <div>
            <label htmlFor="invoice-client-email" className={labelCls}>Client email (needed to send)</label>
            <input id="invoice-client-email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="client@company.com" maxLength={120} data-testid="invoice-client-email" className={inputCls} />
          </div>
          <div>
            <label htmlFor="invoice-number" className={labelCls}>Invoice # (auto if blank)</label>
            <input id="invoice-number" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="INV-2026-001" maxLength={40} data-testid="invoice-number" className={inputCls} />
          </div>
          <div>
            <label htmlFor="invoice-tax-rate" className={labelCls}>Tax rate %</label>
            <input id="invoice-tax-rate" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} placeholder="8.5" type="number" min="0" max="50" step="0.1" data-testid="invoice-tax-rate" className={inputCls} />
          </div>
          <div>
            <label id="invoice-due-date-label" className={labelCls}>Due date</label>
            <Popover open={dueOpen} onOpenChange={setDueOpen}>
              <PopoverTrigger asChild>
                <button type="button" aria-labelledby="invoice-due-date-label" data-testid="invoice-due-date"
                  className={`${inputCls} flex items-center justify-between gap-2 text-left`}>
                  <span className={dueDate ? "" : "text-mist"}>{dueDate ? fmtDueDate(dueDate) : "Pick a due date"}</span>
                  <CalendarDays className="w-3.5 h-3.5 text-mist shrink-0" strokeWidth={1.5} />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto p-0 bg-white border-hairline rounded-lg shadow-[0_16px_48px_-16px_rgba(17,17,16,0.25)]" data-testid="invoice-due-date-calendar">
                <Calendar
                  mode="single"
                  selected={dueDate ? new Date(`${dueDate}T00:00:00`) : undefined}
                  onSelect={(d) => {
                    setDueDate(d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "");
                    setDueOpen(false);
                  }}
                  classNames={CALENDAR_CLASSNAMES}
                  initialFocus
                />
                {dueDate && (
                  <div className="border-t border-hairline p-2 flex justify-end">
                    <button type="button" data-testid="invoice-due-date-clear"
                      onClick={() => { setDueDate(""); setDueOpen(false); }}
                      className="border border-hairline rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-mist hover:text-[#FF3333] hover:border-[#FF3333]/40 transition-colors duration-200">
                      Clear date
                    </button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <label htmlFor="invoice-payment-terms" className={labelCls}>Payment terms</label>
            <input id="invoice-payment-terms" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} placeholder="Net 14 — wire or ACH" maxLength={120} data-testid="invoice-payment-terms" className={inputCls} />
          </div>
        </div>

        <div className="space-y-2" data-testid="invoice-items">
          <div className="hidden sm:grid sm:grid-cols-[1fr_76px_104px_36px] gap-2 px-0.5">
            <span className={labelCls}>Line items *</span>
            <span className={labelCls}>Qty</span>
            <span className={labelCls}>Unit price</span>
            <span />
          </div>
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-2 flex-wrap sm:flex-nowrap sm:grid sm:grid-cols-[1fr_76px_104px_36px]" data-testid={`invoice-item-${i}`}>
              <input
                value={item.description}
                onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))}
                placeholder="Line item description *"
                maxLength={120}
                aria-label={`Line item ${i + 1} description`}
                data-testid={`invoice-item-desc-${i}`}
                className={`${inputCls} w-full sm:w-auto min-w-0`}
              />
              <input
                value={item.quantity}
                onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))}
                type="number" min="0.01" step="0.01" placeholder="Qty"
                aria-label={`Line item ${i + 1} quantity`}
                data-testid={`invoice-item-qty-${i}`}
                className={`${inputCls} w-20 sm:w-full shrink-0`}
              />
              <input
                value={item.unit_price}
                onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, unit_price: e.target.value } : x)))}
                type="number" min="0" step="0.01" placeholder="0.00"
                aria-label={`Line item ${i + 1} unit price`}
                data-testid={`invoice-item-price-${i}`}
                className={`${inputCls} flex-1 sm:flex-none sm:w-full min-w-0`}
              />
              <button
                type="button"
                data-testid={`invoice-item-remove-${i}`}
                onClick={() => setItems(items.filter((_, j) => j !== i))}
                disabled={items.length === 1}
                className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-mist hover:text-[#FF3333] hover:bg-black/5 disabled:opacity-30 transition-colors duration-300"
                aria-label="Remove line item"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          ))}
          <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
            <button type="button" data-testid="invoice-add-item" onClick={() => setItems([...items, newItem()])} disabled={items.length >= 50}
              className="flex items-center gap-1.5 border border-dashed border-hairline rounded-full px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-azure hover:text-ink hover:border-azure disabled:opacity-30 transition-colors duration-300">
              <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Add line item
            </button>
            <div className="font-mono text-xs text-fog text-right space-y-0.5" data-testid="invoice-total">
              <p>Subtotal <span className="text-ink tabular-nums">{money(subtotal)}</span></p>
              {tax > 0 && <p data-testid="invoice-tax-line">Tax {(parseFloat(taxRate) || 0)}% <span className="text-ink tabular-nums">{money(tax)}</span></p>}
              <p>Total <span className="text-ink font-semibold tabular-nums">{money(subtotal + tax)}</span></p>
            </div>
          </div>
        </div>

        <div>
          <label htmlFor="invoice-notes" className={labelCls}>Notes for the client (optional)</label>
          <textarea
            id="invoice-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Payment terms, thanks, due date…"
            maxLength={600}
            rows={2}
            data-testid="invoice-notes"
            className={`${inputCls} resize-none leading-relaxed`}
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <button type="button" data-testid="invoice-download-button" onClick={download} disabled={!valid || !!busy}
            className="flex-1 h-11 rounded-full v4-btn-primary text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-300">
            {busy === "download" ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Download className="w-4 h-4" strokeWidth={1.5} />}
            Download invoice PDF
          </button>
          <button type="button" data-testid="invoice-email-button" onClick={emailInvoice} disabled={!valid || !!busy || !clientEmail.trim() || (guest && !token)}
            className="flex-1 h-11 rounded-full border border-edge text-ink text-sm font-semibold flex items-center justify-center gap-2 hover:border-ink disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-300">
            {busy === "email" ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Mail className="w-4 h-4" strokeWidth={1.5} />}
            Email to client
          </button>
        </div>

        {guest && !token && (
          <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-mist" data-testid="invoice-guest-hint">
            Your first invoice download is free — sending by email lives in the workspace
          </p>
        )}
        {!valid && (
          <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-mist" data-testid="invoice-missing-hint">
            Still needed: {missing.join(", ")}
          </p>
        )}
        {valid && !clientEmail.trim() && (
          <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-mist" data-testid="invoice-email-hint">
            Add the client email above to enable sending
          </p>
        )}

        {error && <p className="text-xs text-[#FF3333] font-medium" data-testid="invoice-error">Error — {error}</p>}
        {sentLink && (
          <div className="flex items-center gap-2 border border-hairline rounded-lg bg-white px-3.5 py-2.5" data-testid="invoice-sent-link">
            <span className="font-mono text-[10px] text-fog truncate flex-1">{sentLink}</span>
            <button type="button" onClick={copyLink} data-testid="invoice-copy-link"
              className="shrink-0 flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.12em] text-azure">
              {copied ? <Check className="w-3.5 h-3.5" strokeWidth={2} /> : <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />} Copy
            </button>
          </div>
        )}

        {guest && guestDone && (
          <div className="pt-3 border-t border-hairline flex items-center justify-between gap-3 flex-wrap" data-testid="invoice-guest-unlock">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-fog">That one was free — unlimited invoices live in the workspace</p>
            <a href="#pricing" className="font-mono text-[11px] uppercase tracking-[0.15em] text-azure hover:text-ink transition-colors duration-300">
              Unlock workspace →
            </a>
          </div>
        )}
      </div>
    </motion.div>
  );
};
